import axios from 'axios';
import { auth } from '../firebase';

const BACKEND_URL = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001')
  : '';

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

const getAuthToken = async (): Promise<string> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User not authenticated');
  }
  return await currentUser.getIdToken();
};

// Create a new thread for a conversation
export const createThread = async (): Promise<string> => {
  try {
    const idToken = await getAuthToken();
    const response = await axios.post(
      `${BACKEND_URL}/api/ai/chat`,
      { action: 'create_thread' },
      {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.data.threadId;
  } catch (error) {
    console.error('Error creating thread:', error);
    throw error;
  }
};

// Send a message to an existing thread
export const sendMessageToThread = async (threadId: string, message: string): Promise<string> => {
  try {
    console.log('🔄 Sending message to thread via backend...');
    const idToken = await getAuthToken();
    
    const response = await axios.post(
      `${BACKEND_URL}/api/ai/chat`,
      {
        action: 'send_message',
        threadId,
        message
      },
      {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        timeout: 120000 // 2 minutes
      }
    );
    
    console.log('✅ Run completed successfully, retrieving response...');
    return response.data.data.message;
  } catch (error) {
    console.error('❌ Error calling backend API:', error);
    if (axios.isAxiosError(error)) {
      console.error('🌐 API Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
    }
    throw error;
  }
};

// Legacy function for backward compatibility
export const sendMessageToOpenAI = async (messages: Message[]): Promise<string> => {
  const threadId = await createThread();
  const lastMessage = messages[messages.length - 1];
  return await sendMessageToThread(threadId, lastMessage.content);
};

// Categorize conversation and generate hashtags
export const categorizeConversation = async (conversationContent: string): Promise<string[]> => {
  const detectLanguage = (content: string): 'en' | 'zh-CN' | 'es' | 'fr' => {
    const firstUserMessage = content.split('\n\n').find(line => line.startsWith('user:'));
    if (!firstUserMessage) return 'en';
    const messageText = firstUserMessage.replace('user:', '').trim();
    if (/[\u4e00-\u9fff]/.test(messageText)) return 'zh-CN';
    if (/[áéíóúñü]/.test(messageText.toLowerCase())) return 'es';
    if (/[àâäéèêëïîôöùûüÿç]/.test(messageText.toLowerCase())) return 'fr';
    return 'en';
  };

  const detectedLanguage = detectLanguage(conversationContent);

  try {
    const idToken = await getAuthToken();
    // Re-using the /api/ai/intent endpoint or a specific categorization logic
    // We can just use the chat endpoint with a system prompt for categorization
    const threadId = await createThread();
    
    // Using Spanish/French/Chinese instructions depending on language
    let promptLanguage = 'English';
    if (detectedLanguage === 'zh-CN') promptLanguage = 'Chinese';
    if (detectedLanguage === 'es') promptLanguage = 'Spanish';
    if (detectedLanguage === 'fr') promptLanguage = 'French';
    
    const prompt = `Categorize this conversation and provide 1-3 relevant hashtags in ${promptLanguage}. Use standard categories like health, behavior, training, nutrition, care. Reply ONLY with the hashtags separated by spaces (e.g. #health #training).\n\n${conversationContent}`;
    
    const hashtagsString = await sendMessageToThread(threadId, prompt);
    return hashtagsString.split(' ').filter((tag: string) => tag.startsWith('#'));
  } catch (error) {
    console.error('Error categorizing conversation:', error);
    const defaultHashtags = {
      'en': ['#general'],
      'zh-CN': ['#一般'],
      'es': ['#general'],
      'fr': ['#général']
    };
    return defaultHashtags[detectedLanguage] || ['#general'];
  }
}; 